import { useRef, useLayoutEffect, useState, useEffect } from "react";

/** Measure + live-resize */
const useMeasuredSize = (ref) => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const cr = entry.contentRect;
      setSize({ width: cr.width, height: cr.height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return size;
};

/**
 * Solve for exponents nLong and nShort so that the 45° corner inset is equal
 * on both axes AND the geometric mean of the exponents remains ~ baseN.
 *
 * We solve for u in (0, 1/a), where:
 *   dx(45°) = rx * (1 - s^(2/nX)), dy(45°) = ry * (1 - s^(2/nY)), s = sqrt(1/2)
 * Equal-cut condition + nX*nY = baseN^2  ==>  find u with:
 *   ln(1 - u) * ln(1 - a*u) = (ln 2)^2 / baseN^2    (monotone in u)
 * Then:
 *   nLong  = (-ln 2) / ln(1 - u)
 *   nShort = (-ln 2) / ln(1 - a*u)
 */
function equalizeExponentsAt45({ longOverShort, baseN }) {
  const EPS = 1e-6;
  const a = Math.max(longOverShort, 1 + EPS); // safety
  const C = -Math.log(2); // 2*ln(sqrt(1/2)) = -ln 2
  const target = (C * C) / (baseN * baseN); // > 0

  // u in (0, 1/a)
  let lo = 1e-6;
  let hi = Math.min(0.999, 1 / a - 1e-6);
  if (!(hi > lo)) {
    // a is ~1 or weird edge -> no need to skew
    return { nLong: baseN, nShort: baseN };
  }

  const f = (u) => {
    const l1 = Math.log(1 - u);    // < 0
    const l2 = Math.log(1 - a * u); // < 0
    return l1 * l2; // > 0 and ↑ with u
  };

  // If baseN is very large, target is tiny; if baseN small, target is large.
  // Bisection: find u where f(u) ~ target
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (f(mid) < target) {
      lo = mid; // need larger product -> increase u
    } else {
      hi = mid;
    }
  }

  const u = (lo + hi) / 2;
  const nLong = C / Math.log(1 - u);
  const nShort = C / Math.log(1 - a * u);
  return { nLong, nShort };
}

const SquircleDiv = ({
  // Overall squircleness knob (keep your semantics). 0.6–1.2 are typical.
  squircle = 0.6,
  // Optional per-axis overrides; if provided, we respect them.
  hCurve,
  vCurve,
  // Keep corners visually equal when width ≠ height
  autoAdjust = true,

  // Visuals
  borderColor = "none",
  borderWidth = 2,
  shadowColor = "rgba(0,0,0,0.15)",
  shadowSpread = 16,

  // Content
  children,
  className = "",
  style = {},
  samples = 500,
  ...rest
}) => {
  const wrapperRef = useRef(null);
  const { width, height } = useMeasuredSize(wrapperRef);

  // Stable unique filter id
  const filterIdRef = useRef(
    `squircleOuterGlow-${Math.random().toString(36).slice(2)}`
  );
  const filterId = filterIdRef.current;

  const isMeasured = width > 0 && height > 0;

  // Derived drawing state
  let cssPolygon = "";
  let ptsString = "";
  let svgW = 0;
  let svgH = 0;
  let bleed = 0;

  if (isMeasured) {
    const rx = width / 2;
    const ry = height / 2;

    // --- Base exponent(s) from props (keep original semantics) ---
    const toN = (val) => 4 * Math.max(0.05, val); // your old scaling
    const baseNX = toN(hCurve ?? squircle);
    const baseNY = toN(vCurve ?? squircle);

    let nX = baseNX;
    let nY = baseNY;

    // --- Equalize the 45° “cut” only when BOTH hCurve and vCurve are absent ---
    if (hCurve == null && vCurve == null && autoAdjust) {
      if (width === height) {
        nX = nY = toN(squircle);
      } else {
        const longIsX = width > height;
        const longOverShort = longIsX ? width / height : height / width;

        // keep geometric mean ≈ baseN to preserve your squircle “feel”
        const baseN = toN(squircle);

        const { nLong, nShort } = equalizeExponentsAt45({
          longOverShort,
          baseN,
        });

        // clamp to sane bounds
        const MIN_N = 0.5;
        const MAX_N = 64;
        const clamp = (v) => Math.min(MAX_N, Math.max(MIN_N, v));
        const nL = clamp(nLong);
        const nS = clamp(nShort);

        if (longIsX) {
          nX = nL;
          nY = nS;
        } else {
          nX = nS;
          nY = nL;
        }
      }
    }

    // --- Build polygon points (superellipse-ish parametric form) ---
    const pts = Array.from({ length: samples }, (_, i) => {
      const t = (2 * Math.PI * i) / samples;
      const ct = Math.cos(t);
      const st = Math.sin(t);
      const x = rx + rx * Math.sign(ct) * Math.pow(Math.abs(ct), 2 / nX);
      const y = ry + ry * Math.sign(st) * Math.pow(Math.abs(st), 2 / nY);
      return [x, y];
    });

    ptsString = pts.map(([x, y]) => `${x},${y}`).join(" ");
    cssPolygon = pts
      .map(([x, y]) => `${(x / width) * 100}% ${(y / height) * 100}%`)
      .join(", ");

    bleed = Math.ceil(shadowSpread + borderWidth);
    svgW = width + bleed * 2;
    svgH = height + bleed * 2;
  }

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: "relative",
        display: "block",
        overflow: "visible",
        ...style,
      }}
      {...rest}
    >
      {/* Shadow + border SVG (only after we know size) */}
      {isMeasured && (
        <svg
          width={svgW}
          height={svgH}
          style={{
            position: "absolute",
            top: -bleed,
            left: -bleed,
            overflow: "visible",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <defs>
            <filter id={filterId} x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur
                in="SourceAlpha"
                stdDeviation={shadowSpread}
                result="blur"
              />
              <feComposite
                in="blur"
                in2="SourceAlpha"
                operator="out"
                result="outer"
              />
              <feFlood floodColor={shadowColor} result="flood" />
              <feComposite in="flood" in2="outer" operator="in" result="shadow" />
              <feMerge>
                <feMergeNode in="shadow" />
                <feMergeNode in="shadow" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer glow */}
          <g transform={`translate(${bleed}, ${bleed})`}>
            <polygon
              points={ptsString}
              fill="black"
              stroke="none"
              filter={`url(#${filterId})`}
            />
          </g>

          {/* Border stroke */}
          <g transform={`translate(${bleed}, ${bleed})`}>
            <polygon
              points={ptsString}
              fill="none"
              stroke={borderColor}
              strokeWidth={borderWidth}
              strokeLinejoin="round"
              shapeRendering="geometricPrecision"
            />
          </g>
        </svg>
      )}

      {/* Content clipped to the squircle */}
      <div
        className={className}
        style={{
          position: "relative",
          width: "100%",
          minHeight: "100%",
          ...(isMeasured && {
            clipPath: `polygon(${cssPolygon})`,
            WebkitClipPath: `polygon(${cssPolygon})`,
          }),
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SquircleDiv;
